import { useRef, useEffect, useCallback } from 'react'
import { videoDisplayRect, normalizePoint } from './videoRect'
import { useRemoteControlStore } from '../../stores/remoteControlStore'
import type { ControlPacket } from '../../services/remoteControl/protocol'

interface Props {
  /** LiveKit identity of the sharer (the controllee) — the publish target. */
  targetIdentity: string
}

export default function RemoteControlOverlay({ targetIdentity }: Props) {
  const session = useRemoteControlStore((s) => s.session)
  const sendInput = useRemoteControlStore((s) => s.sendInput)
  const ref = useRef<HTMLDivElement>(null)
  const active =
    session.state === 'active' &&
    session.role === 'controller' &&
    session.peerUserId === targetIdentity

  const send = useCallback((p: ControlPacket) => sendInput(targetIdentity, p), [sendInput, targetIdentity])

  const toNorm = useCallback((clientX: number, clientY: number) => {
    const el = ref.current
    if (!el) return null
    const rect = el.getBoundingClientRect()
    const video = el.parentElement?.querySelector('video') as HTMLVideoElement | null
    const vr = videoDisplayRect(rect.width, rect.height, video?.videoWidth || 0, video?.videoHeight || 0)
    return normalizePoint(clientX, clientY, { left: rect.left, top: rect.top }, vr)
  }, [])

  useEffect(() => {
    if (!active) return
    const el = ref.current
    if (!el) return
    let pending: { x: number; y: number } | null = null
    let raf = 0
    const flush = () => { raf = 0; if (pending) { send({ t: 'mv', x: pending.x, y: pending.y }); pending = null } }
    const onMove = (e: PointerEvent) => { const n = toNorm(e.clientX, e.clientY); if (!n) return; pending = n; if (!raf) raf = requestAnimationFrame(flush) }
    const onDown = (e: PointerEvent) => { e.preventDefault(); const n = toNorm(e.clientX, e.clientY); if (n) send({ t: 'btn', x: n.x, y: n.y, b: (e.button as 0 | 1 | 2), down: true }) }
    const onUp = (e: PointerEvent) => { e.preventDefault(); const n = toNorm(e.clientX, e.clientY); if (n) send({ t: 'btn', x: n.x, y: n.y, b: (e.button as 0 | 1 | 2), down: false }) }
    const onWheel = (e: WheelEvent) => { e.preventDefault(); send({ t: 'wheel', dx: e.deltaX, dy: e.deltaY }) }
    const onContext = (e: Event) => e.preventDefault()
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('contextmenu', onContext)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('contextmenu', onContext)
    }
  }, [active, send, toNorm])

  useEffect(() => {
    if (!active) return
    const el = ref.current
    el?.focus()
    const onKey = (down: boolean) => (e: KeyboardEvent) => {
      if (e.key === 'Escape' && down) { useRemoteControlStore.getState().end('controller-release'); return }
      e.preventDefault()
      send({ t: 'key', code: e.code, down })
      if (down && e.key.length === 1) send({ t: 'text', v: e.key })
    }
    const kd = onKey(true)
    const ku = onKey(false)
    el?.addEventListener('keydown', kd)
    el?.addEventListener('keyup', ku)
    return () => { el?.removeEventListener('keydown', kd); el?.removeEventListener('keyup', ku) }
  }, [active, send])

  if (!active) return null
  return (
    <div
      ref={ref}
      tabIndex={0}
      className="absolute inset-0 z-20 cursor-none outline-none"
      style={{ boxShadow: 'inset 0 0 0 2px rgba(88,101,242,0.85)' }}
      aria-label="Remote control capture area"
    />
  )
}
